import { useCallback, useMemo, useRef } from 'react';
import { useDesignStore } from '../store/design.store';
import {
  getProject,
  useUpdateProject,
} from '@client/store/project/project.hook';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { Coordinate } from '@shared/models/geography.model';
import { useProjectStateStore } from '@client/store/project/project.store';
import { Project } from '@shared/models/project.model';
import type { ProjectEventActivity } from '@shared/models/project-event.model';
import type { LocationChangeReason } from '../models/location-change.model';
import { getBuilding } from '@shared/helpers/recursive-element.helpers';
import { updateBuilding } from '@shared/helpers/project.helpers';
import { ValidatedValue } from '@shared/validation/validation.models';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  getBuildingById,
  getBuildingStoreys,
} from '@shared/helpers/design/storey.helpers';
import { DesignExpressionVariables } from '@shared/models/design/design-expression-variables.model';
import { getDesignExpressionVariables } from '@shared/helpers/design/expressions/design-expression-variables.helpers';
import {
  areBlueprintStoreyAssignmentsEqual,
  getBlueprintUnderlaysInView,
  getNormalizedBlueprintUnderlays,
  removeBlueprintUnderlayById,
  resolveActiveBlueprintUnderlay,
  updateBlueprintUnderlayById,
  withBlueprintUnderlays,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import { useFetchPvgisData } from '@client/store/pvgis';
import { isAuthDisabled } from '@client/providers/AuthDisabledProvider';
import {
  clearBuildingCandidate,
  getBuildingCandidate,
  getBuildingCandidateStoreyCount,
  getBuildingCandidateTargetGfa,
  useBuildingCandidate,
  useManualBlueprintUnderlayId,
  useIsBuildingSelected,
  useSelectedBuildingId,
  useSelectedStoreyId,
} from '../store/design-store.hooks';
import {
  resolveBuildingCandidatePersistence,
  toSubmittedDesignSnapshot,
  withBuildingCandidate,
} from '../helpers/building-candidate.helpers';
import amplitudeLog from '@client/amplitude';
import { AMP_DESIGN_PDF_UNDERLAY_DELETE } from '@shared/constants';
import { rememberBlueprintRaster } from '../helpers/blueprint-underlay-raster.cache';
import {
  BLUEPRINT_UNDERLAY_PERSIST_FAILED,
  didBlueprintUnderlaysPersist,
  mergePreparedBlueprintUnderlays,
  persistBlueprintUnderlayMetadata,
  prepareBlueprintUnderlaysForPersist,
  resolveBlueprintUnderlaysPersistInput,
  type BlueprintUnderlaysPersistInput,
} from '../helpers/blueprint-underlay-persist.helpers';
import {
  enqueueBuildingDesignPersist,
  runSerializedDesignPersist,
} from '../helpers/design-persist-lock.helpers';
import { assertDesignPersistProjectIsActive } from '../helpers/design-persist-project.helpers';
import {
  adoptDesignSnapshotRaceAnchor,
  rebaseDesignSnapshotOnFresh,
  resolveDesignSnapshotRaceBase,
  runSerializedDesignSnapshotPersist,
  toDesignSnapshotPersistShape,
} from '../helpers/design-snapshot-persist.helpers';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';

type BuildingDesignUpdate =
  | ValidatedValue<BuildingDesign>
  | ((design: BuildingDesign) => ValidatedValue<BuildingDesign>);

const BLUEPRINT_UNDERLAY_PROJECT_CHANGED =
  'Blueprint underlay update was cancelled because the active project changed.';
const DESIGN_UPDATE_PROJECT_CHANGED =
  'Design update was cancelled because the active project changed.';

/**
 * Cancel blueprint persistence when its initiating project is no longer active.
 */
const assertBlueprintUnderlayProjectIsActive = (
  projectId: Project['id'],
): void =>
  assertDesignPersistProjectIsActive({
    projectId,
    errorMessage: BLUEPRINT_UNDERLAY_PROJECT_CHANGED,
  });

/**
 * Get persisted {@link BuildingDesign} from a project.
 * @param project Project with building design.
 * @returns Building design.
 */
const getBuildingDesignFromProject = (project: Project): BuildingDesign =>
  getBuilding(project).design;

/**
 * Get the effective Design-tab model without causing rerenders. An unsaved
 * building candidate is included for local drawing callbacks.
 * @returns Persisted design plus any active candidate
 */
export const getBuildingDesign = (): BuildingDesign =>
  withBuildingCandidate(
    getBuildingDesignFromProject(getProject()),
    getBuildingCandidate(),
  );

/**
 * Get the effective Design-tab model, including an unsaved building candidate.
 * @returns Persisted design plus any active candidate
 */
export const useBuildingDesign = (): BuildingDesign => {
  const persistedDesign = useProjectStateStore(({ project }) =>
    getBuildingDesignFromProject(project),
  );
  const candidate = useBuildingCandidate();
  return useMemo(
    () => withBuildingCandidate(persistedDesign, candidate),
    [persistedDesign, candidate],
  );
};

/**
 * Check if the project has a building design
 * @returns true if the project has a building design, false otherwise
 */
export const useHasBuildingDesign = (): boolean => {
  return useProjectStateStore(({ project }) =>
    guards.is.defined(getBuilding(project).design),
  );
};

/**
 * Get only the building location to avoid rerendering on other design changes
 */
export const useBuildingLocation = (): Coordinate => {
  return useProjectStateStore(
    ({ project }) => getBuildingDesignFromProject(project).location,
  );
};

/**
 * True when two underlay lists match for React selection (id + raster + placement).
 */
const areBlueprintUnderlayListsEqual = (
  left: readonly BlueprintUnderlay[],
  right: readonly BlueprintUnderlay[],
): boolean => {
  if (left === right) return true;
  if (left.length !== right.length) return false;
  return left.every((underlay, index) => {
    const other = right[index];
    if (!other) return false;
    if (underlay === other) return true;
    return (
      underlay.id === other.id &&
      underlay.buildingId === other.buildingId &&
      underlay.imageRef === other.imageRef &&
      underlay.imageDataUrl === other.imageDataUrl &&
      underlay.visible === other.visible &&
      underlay.opacity === other.opacity &&
      underlay.widthMeters === other.widthMeters &&
      underlay.heightMeters === other.heightMeters &&
      underlay.rotationDeg === other.rotationDeg &&
      underlay.center[0] === other.center[0] &&
      underlay.center[1] === other.center[1] &&
      underlay.drawingScaleSource === other.drawingScaleSource &&
      underlay.calibration === other.calibration &&
      areBlueprintStoreyAssignmentsEqual(
        underlay.storeyAssignment,
        other.storeyAssignment,
      )
    );
  });
};

/**
 * Building whose PDF pages the map and panel show: the selected building, or
 * the first one when none is selected (same building as the storey stack).
 */
export const useBlueprintUnderlayBuildingId = (): string | undefined => {
  const selectedBuildingId = useSelectedBuildingId();
  const candidate = useBuildingCandidate();
  // An unsaved candidate is selectable too, so look it up with the candidate.
  return useProjectStateStore(
    ({ project }) =>
      getBuildingById(
        withBuildingCandidate(getBuildingDesignFromProject(project), candidate),
        selectedBuildingId,
      )?.id,
  );
};

/**
 * Plan underlays in view (legacy singular normalized): the site plan while no
 * building is selected, else the selected building's pages. Pages without a
 * building show for every building. Writes go through
 * {@link persistBlueprintUnderlays}, which patches the full list.
 */
export const useBlueprintUnderlays = (): BlueprintUnderlay[] => {
  const buildingId = useBlueprintUnderlayBuildingId();
  const buildingSelected = useIsBuildingSelected();
  return useProjectStateStore(
    ({ project }) =>
      getBlueprintUnderlaysInView(
        getNormalizedBlueprintUnderlays(getBuildingDesignFromProject(project)),
        { buildingId, buildingSelected },
      ),
    areBlueprintUnderlayListsEqual,
  );
};

/**
 * Active plan underlay for the map / command bar (assigned storey, nearest below, or manual override).
 */
export const useActiveBlueprintUnderlay = (): BlueprintUnderlay | undefined => {
  const underlays = useBlueprintUnderlays();
  // getStoreys() is always buildings[0]; the selected storey lives on this stack.
  const storeys = getBuildingStoreys(
    useBuildingDesign(),
    useSelectedBuildingId(),
  );
  const selectedStoreyId = useSelectedStoreyId();
  const manualUnderlayId = useManualBlueprintUnderlayId();

  return useMemo(
    () =>
      resolveActiveBlueprintUnderlay({
        underlays,
        storeys,
        selectedStoreyId,
        manualUnderlayId,
      }),
    [underlays, storeys, selectedStoreyId, manualUnderlayId],
  );
};

/**
 * @deprecated Prefer {@link useActiveBlueprintUnderlay} / {@link useBlueprintUnderlays}.
 * Alias kept for call sites that mean “the underlay currently shown”.
 */
export const useBlueprintUnderlay = (): BlueprintUnderlay | undefined =>
  useActiveBlueprintUnderlay();

/** Options for {@link useUpdateBuildingDesign}. */
export type UpdateBuildingDesignOptions = {
  /**
   * Skip the enrich worker when the project is already enriched.
   * Required for blueprint underlay writes: JPEG data-URLs are large enough
   * to crash the worker (revert + “Unknown worker error”), and underlays do
   * not affect element/recipe enrichment.
   */
  skipEnrichment?: boolean;
  /**
   * Nested design persist already holds {@link runSerializedDesignPersist}.
   * Without this, underlay metadata + imageRef saves deadlock behind themselves.
   */
  skipDesignPersistLock?: boolean;
  /** Runs after the design is in the project store, before enrich/persist. */
  onOptimisticLocalUpdate?: () => void;
  /**
   * Design this save will write, after candidate scaling and origin recenter.
   * Runs before enrich. The pin on this design can differ from the coordinate
   * a caller passed when the footprints were not already centered on the origin.
   */
  onSubmittedDesign?: (design: BuildingDesign) => void;
  /**
   * History entries for this edit; saved and undone with it. A function gets
   * the design the edit applies to, which for a queued write is only known
   * when it runs.
   */
  events?:
    | ProjectEventActivity[]
    | ((current: BuildingDesign) => ProjectEventActivity[]);
};

type UpdateBuildingDesignFn = (
  design: BuildingDesignUpdate,
  options?: UpdateBuildingDesignOptions,
) => Promise<Project>;

/**
 * Feature-usage log when persist drops pages (single delete or clear-all).
 */
const logRemovedBlueprintUnderlays = (
  previous: readonly BlueprintUnderlay[],
  next: readonly BlueprintUnderlay[],
  projectId: number,
): void => {
  const nextIds = new Set(next.map((underlay) => underlay.id));
  const removed = previous.filter((underlay) => !nextIds.has(underlay.id));
  if (!removed.length) return;
  if (!next.length) {
    amplitudeLog(AMP_DESIGN_PDF_UNDERLAY_DELETE, { ProjectId: projectId });
    return;
  }
  for (const underlay of removed) {
    amplitudeLog(AMP_DESIGN_PDF_UNDERLAY_DELETE, {
      UnderlayId: underlay.id,
      ProjectId: projectId,
    });
  }
};

/**
 * Persist a full underlay list onto the current building design.
 * Holds the design persist lock across metadata + JPEG + imageRef so a
 * concurrent geometry import or map-draw snapshot waits instead of sharing
 * `updated_at`. Nested project saves skip the lock.
 */
export const persistBlueprintUnderlays = async (
  underlaysInput: BlueprintUnderlaysPersistInput,
  updateBuildingDesign: UpdateBuildingDesignFn,
): Promise<void> => {
  const projectId = getProject().id;
  assertBlueprintUnderlayProjectIsActive(projectId);
  await runSerializedDesignPersist(async () => {
    assertBlueprintUnderlayProjectIsActive(projectId);
    const previous = getNormalizedBlueprintUnderlays(getBuildingDesign());
    const underlays = resolveBlueprintUnderlaysPersistInput(
      previous,
      underlaysInput,
    );
    // A patcher returns the live list when the edit is a no-op. Skip the PUT
    // so an unchanged scale blur does not clobber a newer queued edit.
    if (underlays === previous) return;
    // Cache JPEGs before the metadata save strips `imageDataUrl` off the document.
    for (const underlay of underlays) {
      if (underlay.imageDataUrl) {
        rememberBlueprintRaster(underlay.id, underlay.imageDataUrl);
      }
    }
    const updateDesignWithoutLock: UpdateBuildingDesignFn = (design, options) =>
      updateBuildingDesign(design, {
        ...options,
        skipDesignPersistLock: true,
      });
    // PUT requires the underlay row to exist; save metadata first for new pages.
    await persistBlueprintUnderlayMetadata(underlays, {
      getDesign: getBuildingDesign,
      updateDesign: updateDesignWithoutLock,
    });
    assertBlueprintUnderlayProjectIsActive(projectId);
    const prepared = await prepareBlueprintUnderlaysForPersist(
      projectId,
      previous,
      underlays,
    );
    assertBlueprintUnderlayProjectIsActive(projectId);
    const merged = mergePreparedBlueprintUnderlays(
      getNormalizedBlueprintUnderlays(getBuildingDesign()),
      prepared,
      previous,
    );
    const designToSave = withBlueprintUnderlays(getBuildingDesign(), merged);
    // Read before the write. Optimistic update clears a promoted candidate
    // before this promise resolves, after the writer has already applied it.
    const candidate = getBuildingCandidate();
    const submittedDesign = toSubmittedDesignSnapshot(designToSave, {
      candidate,
      storeyCount: getBuildingCandidateStoreyCount(),
      targetGfa: getBuildingCandidateTargetGfa(),
      preserveEmptyCandidate: Boolean(
        candidate &&
        getBuildingDesignFromProject(getProject()).buildings.some(
          ({ id }) => id === candidate.id,
        ),
      ),
    }).design;
    await updateDesignWithoutLock(designToSave, { skipEnrichment: true });
    assertBlueprintUnderlayProjectIsActive(projectId);
    const submittedUnderlays = getNormalizedBlueprintUnderlays(submittedDesign);
    if (
      !didBlueprintUnderlaysPersist(
        submittedUnderlays,
        getNormalizedBlueprintUnderlays(getBuildingDesign()),
      )
    ) {
      throw new Error(BLUEPRINT_UNDERLAY_PERSIST_FAILED);
    }
    logRemovedBlueprintUnderlays(previous, merged, projectId);
  });
};

/**
 * Patch one existing underlay by id without restoring a concurrently deleted page.
 */
export const persistBlueprintUnderlayById = async (
  next: BlueprintUnderlay,
  updateBuildingDesign: UpdateBuildingDesignFn,
): Promise<void> => {
  await persistBlueprintUnderlays(
    (live) => updateBlueprintUnderlayById(live, next),
    updateBuildingDesign,
  );
};

/**
 * Remove one underlay by id.
 */
export const removePersistedBlueprintUnderlayById = async (
  id: string,
  updateBuildingDesign: UpdateBuildingDesignFn,
): Promise<void> => {
  await persistBlueprintUnderlays(
    (live) => removeBlueprintUnderlayById(live, id),
    updateBuildingDesign,
  );
};

/**
 * Persist a {@link BuildingDesign}. Pass an updater so the write is applied to
 * the latest design after earlier PUTs in this queue finish (DEV-2576).
 * `onOptimisticLocalUpdate` runs as soon as the next design is in the project
 * store, before enrich and the server PUT.
 */
export const useUpdateBuildingDesign = (): UpdateBuildingDesignFn => {
  const updateProject = useUpdateProject();

  return useCallback<UpdateBuildingDesignFn>(
    async (designOrUpdate, options) => {
      // Capture the candidate now. A route change may discard the UI state
      // before this queued write gets its turn, but a completed footprint must
      // still be able to promote the candidate that received it.
      const candidate = getBuildingCandidate();
      const candidateStoreyCount = getBuildingCandidateStoreyCount();
      const candidateTargetGfa = getBuildingCandidateTargetGfa();
      const runPersist = async (releaseQueue: () => void): Promise<Project> => {
        const persistedDesign = getBuildingDesignFromProject(getProject());
        const preserveEmptyCandidate = Boolean(
          candidate &&
          persistedDesign.buildings.some(({ id }) => id === candidate.id),
        );
        const currentDesign = withBuildingCandidate(persistedDesign, candidate);
        // Overlapping deletes share updated_at. Rebase against the design at
        // that timestamp, not the previous local snapshot (DEV-3857).
        const submittedUpdatedAt = getProject().updated_at;
        const snapshotBase = resolveDesignSnapshotRaceBase({
          projectId: getProject().id,
          updatedAt: submittedUpdatedAt,
          currentDesign: toDesignSnapshotPersistShape(currentDesign),
        });
        const updatedDesign =
          typeof designOrUpdate === 'function'
            ? designOrUpdate(currentDesign)
            : designOrUpdate;

        // TODO: Use new errorHandler
        if (guards.is.error(updatedDesign)) {
          console.error(updatedDesign);
          return getProject();
        }

        // Same-reference no-op from an updater (overlap, unchanged GFA, etc.).
        if (updatedDesign === currentDesign) return getProject();
        const events =
          typeof options?.events === 'function'
            ? options.events(currentDesign)
            : options?.events;

        // Same shape persistBlueprintUnderlays compares after the save.
        const submitted = toSubmittedDesignSnapshot(updatedDesign, {
          candidate,
          storeyCount: candidateStoreyCount,
          targetGfa: candidateTargetGfa,
          preserveEmptyCandidate,
        });
        const incoming = submitted.design;
        options?.onSubmittedDesign?.(incoming);
        // Queue base: strip empty candidates, then the same persist shape as incoming.
        const getPersistQueueDesign = (): BuildingDesign =>
          (() => {
            const livePersistedDesign =
              getBuildingDesignFromProject(getProject());
            const preserveLiveEmptyCandidate = Boolean(
              candidate &&
              livePersistedDesign.buildings.some(
                ({ id }) => id === candidate.id,
              ),
            );
            return toDesignSnapshotPersistShape(
              resolveBuildingCandidatePersistence(
                withBuildingCandidate(livePersistedDesign, candidate),
                candidate,
                { preserveEmptyCandidate: preserveLiveEmptyCandidate },
              ).design,
            );
          })();
        const projectId = getProject().id;
        const persistSnapshot = async (
          nextDesign: BuildingDesign,
        ): Promise<Project> => {
          assertDesignPersistProjectIsActive({
            projectId,
            errorMessage: DESIGN_UPDATE_PROJECT_CHANGED,
          });
          // Set only from onPersisted, which runs after the PUT lands.
          // Superseded enrich and 422 recovery return the live project instead.
          let serverWriteLanded = false;
          // TODO(replay): express as ProjectChange so a 422 race can be replayed.
          const persisted = await updateProject({
            kind: 'snapshot',
            project: updateBuilding(getProject(), { design: nextDesign }),
            rebaseOnRace: (freshProject) => {
              const freshDesign = toDesignSnapshotPersistShape(
                getBuildingDesignFromProject(freshProject),
              );
              const rebased = rebaseDesignSnapshotOnFresh({
                base: snapshotBase,
                incoming: nextDesign,
                fresh: freshDesign,
              });
              return rebased
                ? updateBuilding(freshProject, { design: rebased })
                : undefined;
            },
            skipEnrichment: options?.skipEnrichment,
            events,
            onOptimisticLocalUpdate: () => {
              if (submitted.shouldClearCandidate) {
                const state = useDesignStore.getState();
                const completedId = candidate?.id;
                if (
                  completedId &&
                  state.buildingCandidate?.id === completedId &&
                  state.selectedBuildingId === completedId
                ) {
                  state.selectBuildingOutline(completedId);
                }
                clearBuildingCandidate(completedId);
              }
              options?.onOptimisticLocalUpdate?.();
              // Next Add storey reads this design and cancels the in-flight
              // enrich. Import and underlay still wait on the design-save hold
              // until this save finishes (DEV-3785).
              releaseQueue();
            },
            onPersisted: () => {
              serverWriteLanded = true;
            },
          });
          // The live project can already contain a newer edit that inherited
          // this timestamp. Anchor only the design a landed PUT returned.
          if (
            serverWriteLanded &&
            persisted.updated_at !== submittedUpdatedAt
          ) {
            adoptDesignSnapshotRaceAnchor({
              projectId,
              updatedAt: persisted.updated_at,
              design: toDesignSnapshotPersistShape(
                getBuilding(persisted).design,
              ),
            });
          }
          return persisted;
        };

        if (options?.skipDesignPersistLock) return persistSnapshot(incoming);

        return runSerializedDesignSnapshotPersist({
          projectId,
          incoming,
          getLiveDesign: getPersistQueueDesign,
          persist: persistSnapshot,
        });
      };

      if (options?.skipDesignPersistLock) return runPersist(() => undefined);
      return enqueueBuildingDesignPersist(runPersist);
    },
    [updateProject],
  );
};

/**
 * Update the building location while keeping other properties intact.
 * Skips the live weather fetch when auth is disabled (e2e): CI cannot reach
 * JRC, and PVGIS_DISABLED makes the proxy fail on a cache miss.
 * Resolves with the pin that was saved. Persist places that pin on the
 * combined footprint midpoint, so it can differ from {@link location} when
 * the footprints were not already centered (DEV-3888).
 * Throws when the project that comes back does not have that saved pin, so
 * callers can roll the map origin back. A newer location request resolves
 * to void, including when this save's persist rejects, so that rejection
 * cannot roll the newer origin back. Location is applied to the latest
 * design after PVGIS returns so concurrent geometry writes are not wiped.
 */
type UpdateBuildingLocationFn = (
  location: Coordinate,
  options?: { reason?: LocationChangeReason },
) => Promise<Coordinate | void>;

export const useUpdateBuildingLocation = (): UpdateBuildingLocationFn => {
  const updateBuildingDesign = useUpdateBuildingDesign();
  const fetchPvgisData = useFetchPvgisData();
  const { enqueueSnackbar } = useSnackbar();
  const requestSequence = useRef(0);
  return useCallback<UpdateBuildingLocationFn>(
    async (location, { reason = 'user' } = {}) => {
      const sequence = ++requestSequence.current;
      // Match DataLoaderProxy: e2e must not hit /proxy/pvgis.
      if (!isAuthDisabled()) {
        try {
          await fetchPvgisData(location);
        } catch (error) {
          console.error('PVGIS weather data fetch failed:', error);
        }
      }
      // A later pin move started while PVGIS was loading. Only that latest
      // intent may enter the persist queue; otherwise response timing can make
      // an older drag overwrite the newer location.
      if (sequence !== requestSequence.current) return;
      // Re-read after PVGIS and after the persist queue so concurrent geometry writes stay.
      let submittedLocation = location;
      let project: Project;
      try {
        project = await updateBuildingDesign(
          (design) => ({ ...design, location }),
          {
            // Only a move the user makes is history; re-centring the origin is not.
            events: reason === 'user' ? [{ kind: 'project-location' }] : [],
            onSubmittedDesign: (design) => {
              // Copy. The snapshot can keep this object and mutate it later.
              submittedLocation = {
                lat: design.location.lat,
                lng: design.location.lng,
              };
            },
          },
        );
      } catch (error) {
        // A newer pin or search owns the origin. The pin controller treats a
        // rejection as a failed drag and would move the map back.
        if (sequence !== requestSequence.current) return;
        throw error;
      }
      if (sequence !== requestSequence.current) return;
      const persisted = getBuilding(project).design.location;
      // Compare to the recentered pin this save wrote, not the raw request.
      if (
        persisted.lat !== submittedLocation.lat ||
        persisted.lng !== submittedLocation.lng
      ) {
        enqueueSnackbar('Could not save building location', {
          variant: 'error',
        });
        throw new Error('Failed to persist building location');
      }
      // Detach from the project store so a later map mutation cannot rewrite it.
      return { lat: persisted.lat, lng: persisted.lng };
    },
    [updateBuildingDesign, fetchPvgisData, enqueueSnackbar],
  );
};

export const useDesignExpressionVariables = ():
  DesignExpressionVariables | undefined => {
  const design = useBuildingDesign();
  return useMemo(() => getDesignExpressionVariables(design), [design]);
};
